import { Node, Edge } from "reactflow";
import { ITopicPair } from "src/app/nlp/~store/models/TopicPair";
import { NlpButtonType } from "src/app/nlp/~store/models/Answer";
import {
    GraphData,
    TopicPairParameterJsonKey,
    TopicPairsTree,
} from "../models/common";
import { ButtonsNodeData } from "../models/buttonsNode";
import { buildTopicPairsTree } from "./treeCreation";
import { makeEdges, makeNodes } from "./nodesCreation";
import {
    ALL_TOPIC_PAIR_NEXT_TRIGGER_KEYS,
    START_DIALOG_TRIGGER_NAME,
} from "../constants/common";

export const extractTopicPairParameterJson = (
    pair: ITopicPair,
    key: TopicPairParameterJsonKey
) => (pair.answer?.parametersJson as any)[key];

export const extractAllTopicPairNextTriggers = (pair: ITopicPair) =>
    ALL_TOPIC_PAIR_NEXT_TRIGGER_KEYS.map((key) =>
        extractTopicPairParameterJson(pair, key)
    ).filter((trigger) => Boolean(trigger));

export const extractTopicPairButtons = (pair: ITopicPair) =>
    pair.answer?.buttons ?? [];

export const extractAllButtonsTypesFromTopicPair = (pair: ITopicPair) =>
    extractTopicPairButtons(pair).map((button) => button.type);

export const extractAllButtonsValuesFromTopicPair = (pair: ITopicPair) =>
    extractTopicPairButtons(pair).map((button) => button.value);

// TODO: No Back button in master branch
const isAnyTopicPairButtonTypeTriggerOrBack = (pair: ITopicPair) => {
    const buttonTypes = extractAllButtonsTypesFromTopicPair(pair);
    return buttonTypes.includes(NlpButtonType.Trigger);
};

const isAnyTopicPairNextTriggerExist = (pair: ITopicPair) =>
    ALL_TOPIC_PAIR_NEXT_TRIGGER_KEYS.some(
        (key) => !!extractTopicPairParameterJson(pair, key)
    );

const isTopicPairTriggerNameExistInSomeButton = (
    pair: ITopicPair,
    allPairs: ITopicPair[]
) =>
    allPairs.some(
        (item) =>
            isAnyTopicPairButtonTypeTriggerOrBack(item) &&
            extractAllButtonsValuesFromTopicPair(item).includes(
                pair.triggerName
            )
    );

const isTopicPairTriggerNameExistInSomeNextTrigger = (
    pair: ITopicPair,
    allPairs: ITopicPair[]
) =>
    allPairs.some((item) =>
        ALL_TOPIC_PAIR_NEXT_TRIGGER_KEYS.map((key) =>
            extractTopicPairParameterJson(item, key)
        ).includes(pair.triggerName)
    );

const isTopicPairHasParent = (pair: ITopicPair, allPairs: ITopicPair[]) =>
    isTopicPairTriggerNameExistInSomeButton(pair, allPairs) ||
    isTopicPairTriggerNameExistInSomeNextTrigger(pair, allPairs);

// TODO: Is it needed to check reference to triggerName exist?
const topicPairHasChildren = (pair: ITopicPair, allPairs?: ITopicPair[]) =>
    isAnyTopicPairButtonTypeTriggerOrBack(pair) ||
    isAnyTopicPairNextTriggerExist(pair);

export const findAllRootTopicPairs = (allPairs: ITopicPair[]) =>
    allPairs
        .filter(
            (pair) =>
                pair.triggerName === START_DIALOG_TRIGGER_NAME ||
                (topicPairHasChildren(pair, allPairs) &&
                    !isTopicPairHasParent(pair, allPairs))
        )
        .sort((a) =>
            a.triggerName === START_DIALOG_TRIGGER_NAME ? -1 : 0
        );

export const makeTopicPairsTrees = (
    topicPairs: ITopicPair[]
): TopicPairsTree[] => {
    const trees: TopicPairsTree[] = [];
    const allRootPairs = findAllRootTopicPairs(topicPairs);

    allRootPairs.forEach((rootPair) => {
        const tree = buildTopicPairsTree(topicPairs, rootPair.topicPairId);
        if (tree) {
            trees.push(tree);
        }
    });

    return trees;
};

export const makeGraphData = (tree: TopicPairsTree): GraphData => ({
    nodes: makeNodes(tree),
    edges: makeEdges(tree),
});

export const makeTopicPairsData = (topicPairs: ITopicPair[]): GraphData => {
    const allNodes: Node<ButtonsNodeData>[] = [];
    const allEdges: Edge[] = [];

    const allRootPairs = findAllRootTopicPairs(topicPairs);

    allRootPairs.forEach((rootPair) => {
        const tree = buildTopicPairsTree(topicPairs, rootPair.topicPairId);
        if (tree) {
            allNodes.push(...makeNodes(tree));
            allEdges.push(...makeEdges(tree));
        }
    });

    return { nodes: allNodes, edges: allEdges };
};
